<template>
  <div id="app" class="app" :class="modifiers">
    <transition
      name="screen-slide"
      @after-enter="onScreenEnter"
    >
      <router-view />
    </transition>
    <TheHeader
      class="app__header"
      @back="goBack"
    />
    <TheFooter
      class="app__footer"
    />
    <BaseOverlay
      v-if="isTouchOverlayVisible"
      v-bind="touchOverlay"
      class="app__overlay"
      @click="startInteraction"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { routes } from '@/router/index';

import TheHeader from '@/components/TheHeader.vue';
import TheFooter from '@/components/TheFooter.vue';
import BaseOverlay from '@/components/BaseOverlay.vue';

export const GLOBAL_EVENTS = Object.freeze({
  ROBOT_REPLIC_FINISH: 'ROBOT_REPLIC_FINISH',
  ROBOT_REPLIC_START: 'ROBOT_REPLIC_START',
  USER_REPLIC_FINISH: 'USER_REPLIC_FINISH',
  INTERACTION_STARTED: 'INTERACTION_STARTED',
  INTERACTION_STOPPED: 'INTERACTION_STOPPED',
  SCREEN_READY: 'SCREEN_READY',
  DRIVE_CANCEL: 'DRIVE_CANCEL',
  DRIVE_OBSTACLE: 'DRIVE_OBSTACLE',
  DRIVE_FINISH: 'DRIVE_FINISH',
  DRIVE_START: 'DRIVE_START',
});

export const GLOBAL_ACTIONS = Object.freeze({
  VOLUME_UP: 'ACTION_VOLUME_UP',
  VOLUME_DOWN: 'ACTION_VOLUME_DOWN',
  GO_HOME: 'ACTION_GO_HOME',
  GO_BACK: 'ACTION_GO_BACK',
  GO_NEXT: 'ACTION_GO_NEXT',
  SUBMIT: 'ACTION_SUBMIT',
  RETRY: 'ACTION_RETRY',
  CLEAR: 'ACTION_CLEAR',
});

export default {
  name: 'App',
  components: {
    TheHeader,
    TheFooter,
    BaseOverlay,
  },
  data() {
    return {
      isBackMove: false,
      isTouchOverlayVisible: true,
    };
  },
  computed: {
    modifiers() {
      return {
        app_back_move: this.isBackMove,
      };
    },
    touchOverlay() {
      return {
        icon: {
          name: 'point',
          size: 'xxl',
        },
        title: {
          text: this.$t('main.tap'),
          size: 'xl',
          isNormal: true,
        },
      };
    },
  },
  watch: {
    $route(to, from) {
      const fromIndex = routes.findIndex((route) => route.path === from.path);
      const toIndex = routes.findIndex((route) => route.path === to.path);
      this.isBackMove = fromIndex > toIndex;
    },
  },
  mounted() {
    this.init();
  },
  methods: {
    ...mapActions([
      'clearSession',
    ]),
    showTouchOverlay() {
      this.isTouchOverlayVisible = true;
    },
    hideTouchOverlay() {
      this.isTouchOverlayVisible = false;
    },
    goBack() {
      this.emitAction(GLOBAL_ACTIONS.GO_BACK);
    },
    startInteraction() {
      window.setTimeout(() => {
        this.$robot.interactionService.start();
        this.hideTouchOverlay();
        this.emitAction(GLOBAL_EVENTS.INTERACTION_STARTED);
      }, 250);
    },
    stopInteraction() {
      this.clearSession();
      this.showTouchOverlay();
      this.emitAction(GLOBAL_EVENTS.INTERACTION_STOPPED);
    },
    onScreenEnter() {
      this.emitAction(GLOBAL_EVENTS.SCREEN_READY);
    },
    onKeyboardVisibleChanged(isVisible) {
      if (isVisible) return;
      document.activeElement.blur();
    },
    onDriveStart() {
      this.emitAction(GLOBAL_EVENTS.DRIVE_START);
    },
    onDriveFinish() {
      this.emitAction(GLOBAL_EVENTS.DRIVE_FINISH);
    },
    onDriveObstacle() {
      this.emitAction(GLOBAL_EVENTS.DRIVE_OBSTACLE);
    },
    onDriveCancel() {
      this.emitAction(GLOBAL_EVENTS.DRIVE_CANCEL);
    },
    emitAction(name) {
      const actions = Array.isArray(name) ? name : [name];
      actions.forEach((action) => this.$root.$emit(action));
    },
    init() {
      const robotActions = [
        this.$robot.dialogService.onAction(this.emitAction),
        this.$robot.interactionService.onStop(this.stopInteraction),
        this.$robot.interactionService.onStart(this.startInteraction),
        this.$robot.keyboardService.onChangeVisible(this.onKeyboardVisibleChanged),
        this.$robot.driveService.onStart(this.onDriveStart),
        this.$robot.driveService.onFinish(this.onDriveFinish),
        this.$robot.driveService.onObstacle(this.onDriveObstacle),
        this.$robot.driveService.onCancel(this.onDriveCancel),
      ];
      this.$once('hook:beforeDestroy', () => {
        robotActions.forEach((unsubscribe) => unsubscribe());
      });
    },
  },
};
</script>

<style lang="stylus">
@import '~@/assets/stylus/fonts'
@import '~@/assets/stylus/normalize'
@import '~@/assets/stylus/keyframes'
@import '~@/assets/stylus/blocks'

.app
  position relative
  display block
  width 100%
  height 100%
  font-family $font_primary, system-ui, sans-serif
  font-size calc(1vw + 1vh)
  font-weight 400
  line-height 1
  color $c_text
  overflow hidden

  &__overlay
    fix top left right bottom
    z-index 1000

  &__header
    fix top left right
    height $header_height
    pointer-events none
    z-index 10

  &__footer
    fix bottom left right
    height $footer_height
    pointer-events none
    z-index 10
</style>
